/** @jsx jsx */
import { Button, Grid, IconButton, Spacer } from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { H4, P } from "@cd2/text"
import { IComponentProp } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import React from "react"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { DraggableList } from "../../Components/DraggableList"
import { brandColors } from "../../Components/EOSThemeProvider"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { ProductSelect2 } from "../../Components/ProductSelect2"
import { CustomerSelectList2 } from "../../Components/SelectLists/CustomerSelectList2"
import { icons } from "../../constants/icons"
import { useRedirectRoute } from "../../hooks/useRedirectRoute"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"

interface IFormData {
  id?: string
  price_pennies?: number | null
  productId: string
  customerId?: string
  build_set_items: IFormDataItem[]
}

interface IFormDataItem {
  id: string
  product?: { id: string } | null
  quantity: number | null
  weight: number | null
  optional: boolean
  _destroy?: boolean
}

export function BuildSetsForm({
  initialData,
  afterSubmit,
}: {
  initialData?: Partial<IFormData>
  afterSubmit?: () => Promise<void>
}) {
  const { currentUser } = useGlobalState()
  const branch = currentUser.branch
  const alerts = useSiteAlerts()
  const redirect = useRedirectRoute()

  async function handleSubmit(data: IFormData) {
    try {
      const record = await handleSaveBuildSet(data, initialData)
      if (record.id) {
        alerts.genericSuccess("BOM item saved")
        if (initialData?.id) {
          await afterSubmit?.()
        } else {
          redirect("/stock/bom-items/:id", { id: record.id })
        }
      }
    } catch (error) {
      console.error(error)
      alerts.genericError("Failed to save BOM item")
    }
  }

  return (
    <Form2.Form
      key={JSON.stringify(initialData)}
      initialData={initialData ?? {}}
      onSubmit={handleSubmit}
    >
      <FloatingBox
        header={
          <React.Fragment>
            <H4>Details</H4>
            <P size="small">Leave customer blank to be available to all branch customers.</P>
          </React.Fragment>
        }
      >
        <Form2.WithField<string>
          field="productId"
          render={field => (
            <ProductSelect2
              label="Build item code"
              onChange={value => field.setValue(value?.id)}
              value={field.getValue()}
              branchId={branch.id ?? ""}
              showRequiredStar
            />
          )}
        />
        <Spacer />
        <Form2.WithField<string>
          field="customerId"
          render={field => (
            <CustomerSelectList2
              label="Customer"
              onChange={value => field.setValue(value)}
              value={field.getValue()}
              branchId={branch.id ?? ""}
              isClearable
            />
          )}
        />
        <Spacer size={4} />
        <P size="small">
          Selecting a customer will limit this build item to only be visible when this customer is
          selected on the quote/order
        </P>
        <Spacer />
        <Form2.Money field="price_pennies" label="Override Item Price" asPennies />
        <Spacer size={4} />
        <P size="small">
          Setting this value will add a default value for the item price when added to a quote/order
        </P>
      </FloatingBox>
      <Spacer />
      <ItemField />
      <Spacer />
      <div>
        <Form2.Submit />
      </div>
    </Form2.Form>
  )
}

function ItemField() {
  const itemsField = Form2.useField<IFormDataItem[]>("build_set_items", {
    defaultValue: [
      {
        id: buildSetNewItemId(),
        quantity: 1,
        weight: 0,
        optional: false,
      },
    ],
    subscribe: true,
  })

  function handleAddItem() {
    const currentValue = itemsField.getValue()
    itemsField.setValue([
      ...currentValue,
      {
        id: buildSetNewItemId(),
        quantity: 1,
        weight: currentValue.length,
        optional: false,
      },
    ])
  }
  function handleRemoveItem(id: string) {
    handleUpdateItem(id, { _destroy: true })
  }
  function handleUpdateItem(id: string, data: Partial<IFormDataItem>) {
    itemsField.setValue(
      itemsField.getValue().map(item => {
        if (item.id === id) {
          return { ...item, ...data }
        }
        return item
      }),
    )
  }
  function getErrorFor(id: string) {
    return itemsField.getError()?.[id]
  }

  const columns: IComponentProp<typeof Grid, "columns"> = [24, { fr: 1 }, { fr: 1 }, 38]

  const sortedItems = itemsField
    .getValue()
    .filter(x => !x._destroy)
    .sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0))
  return (
    <FloatingBox
      header={
        <React.Fragment>
          <H4>Build Items</H4>
          <P size="small">
            On saving this build item, items in the list below without a product will be removed
            from the build item.
          </P>
        </React.Fragment>
      }
      css={css`
        --floating-box-child-padding: 16px 0;
      `}
    >
      <Grid
        columns={columns}
        gap={8}
        css={css`
          padding: 0 4px;
        `}
      >
        <div />
        <P weight={600} blockSpacing={0}>
          Product
        </P>
        <P weight={600} blockSpacing={0}>
          Quantity
        </P>
        <div />
      </Grid>
      <Spacer />

      <DraggableList
        keyExtractor={item => item.id}
        items={sortedItems}
        onOrderChange={newItems =>
          itemsField.setValue(newItems.map((item, index) => ({ ...item, weight: index })))
        }
        renderItem={(item, DragHandle, isDragging) => {
          const isFirstItem = sortedItems[0].id === item.id
          return (
            <div
              key={item.id}
              css={css`
                padding: 16px 4px;
                ${isFirstItem || isDragging
                  ? `border-top: 1px solid ${brandColors.lighterNight};`
                  : ""}
                border-bottom: 1px solid ${brandColors.lighterNight};
                background-color: white;
              `}
            >
              <Grid columns={columns} gap={8}>
                <Grid.Area justifySelf="center" alignSelf="center" row={[1, 2]}>
                  <DragHandle />
                </Grid.Area>

                <div>
                  <ProductSelect2
                    onChange={product => handleUpdateItem(item.id, { product: product })}
                    value={item.product?.id}
                    error={getErrorFor(item.id)?.product}
                  />
                </div>

                <div>
                  <Fields.Number2
                    onChange={number => handleUpdateItem(item.id, { quantity: number })}
                    value={item.quantity}
                    error={getErrorFor(item.id)?.quantity}
                  />
                </div>

                <div>
                  <IconButton
                    buttonStyle="outline"
                    icon={icons.x}
                    onClick={() => handleRemoveItem(item.id)}
                    buttonColor="error"
                    square
                  />
                </div>

                <Grid.Area column={[2, 3]} row={2}>
                  <Spacer size={4} />
                  <Fields.Checkbox
                    label="Optional"
                    onChange={optional => handleUpdateItem(item.id, { optional })}
                    value={item.optional}
                    error={getErrorFor(item.id)?.optional}
                  />
                </Grid.Area>
              </Grid>
            </div>
          )
        }}
      />
      <Spacer />
      <div
        css={css`
          padding: 0 16px;
        `}
      >
        <ButtonsWrapper>
          <Button onClick={() => handleAddItem()}>Add item</Button>
        </ButtonsWrapper>
      </div>
    </FloatingBox>
  )
}
const NEW_ITEM_PREFIX = "NEW_"
export function buildSetNewItemId() {
  return `${NEW_ITEM_PREFIX}${Math.random().toString()}`
}
function isNewId(id: string) {
  return id.startsWith(NEW_ITEM_PREFIX)
}
export async function handleSaveBuildSet(data: IFormData, _originalRecord?: Partial<IFormData>) {
  const updateData = {
    product: { set: data.productId },
    customer: { set: data.customerId ?? null },
    price_pennies: data.price_pennies,
    build_set_items: data.build_set_items
      .map(item => {
        if (item._destroy) {
          return isNewId(item.id) ? null : { destroy: item.id }
        }
        if (!item.product) {
          return null
        }
        if (isNewId(item.id)) {
          return {
            create: {
              product: { set: item.product?.id ?? null },
              weight: item.weight,
              quantity: item.quantity,
              optional: item.optional,
            },
          }
        }
        return {
          update: {
            id: item.id,
            product: { set: item.product?.id ?? null },
            weight: item.weight,
            quantity: item.quantity,
            optional: item.optional,
          },
        }
      })
      .filter(Boolean),
  }
  let result: { id: string } | undefined = undefined
  if (data.id) {
    result = await window.skeem.mutate({ build_sets: { update: { ...updateData, id: data.id } } })
  } else {
    const me = await window.skeem.fetchMe<{ branch: { id: string }; id: string }>(["branch"])
    result = await window.skeem.mutate({
      build_sets: {
        create: { ...updateData, created_by: { set: me.id }, branch: { set: me.branch.id } },
      },
    })
  }
  return result
}
